import {
  Box, Grid, Typography, Skeleton, Paper, Chip,
  IconButton, LinearProgress, Tooltip, Button, Stack,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import MarketHolidaysWidget from '../dashboard/MarketHolidaysWidget';
import CelebrationsWidget from '../../routes/dashboard/CelebrationsWidget';
import { useGetSingleEmployeeDetails } from '../../hooks/useEmployees';
import { useSingleEmpLeaveDetails } from '../../hooks/useLeaves';
import { useEmployeeCycleAttendance, useEmployeeAttendanceHistory } from '../../hooks/useAttendance';
import { getCurrentCycle, getCycleByParam, getCycleByStart, MONTH_NAMES } from '../../utils/attendanceCycle';
import { STATUS_META } from '../Attendance/attendanceLegend';
import { leaveContext } from '../../context/leavecontext';
import { AuthContext } from '../../context/logeduser';

import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import InsightsIcon from '@mui/icons-material/Insights';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

// ---- Visual tokens (FORTHEYE-style soft cards) --------------------------------
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
  height: '100%',
};
const ACCENT = '#2f6bff';
// Mockup's tabular-number face — used for every big numeric value on the page.
const NUM_FONT = "'Space Grotesk', 'Outfit', sans-serif";

const DOT_COLOR = {
  PRESENT: '#16a34a',
  ON_DUTY: '#0d9488',
  COMP_OFF: '#f59e0b',
  HALF_DAY: '#eab308',
  LEAVE: '#ef4444',
  ABSENT: '#ef4444',
  HOLIDAY: '#cbd5e1',
};

const pad2 = (n) => String(n).padStart(2, '0');
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ---- Small helpers ------------------------------------------------------------
const parseFlexibleDate = (str) => {
  if (!str) return null;
  const parts = String(str).split(/[-/]/);
  if (parts.length !== 3) return null;
  let y, m, d;
  if (parts[0].length === 4) { y = +parts[0]; m = +parts[1]; d = +parts[2]; }
  else { d = +parts[0]; m = +parts[1]; y = +parts[2]; }
  if ([y, m, d].some(Number.isNaN) || m < 1 || m > 12) return null;
  return new Date(y, m - 1, d);
};

const durationToMinutes = (raw) => {
  if (!raw) return 0;
  const m = String(raw).match(/^(\d+):(\d{2})/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : 0;
};

const formatHours = (minutes) => {
  if (!minutes) return '0';
  const h = minutes / 60;
  return h >= 10 ? String(Math.round(h)) : h.toFixed(1).replace(/\.0$/, '');
};

const todayISO = () => {
  const n = new Date();
  return `${n.getFullYear()}-${pad2(n.getMonth() + 1)}-${pad2(n.getDate())}`;
};

const monthDates = (year, month) => {
  const last = new Date(year, month, 0).getDate();
  return Array.from({ length: last }, (_, i) => `${year}-${pad2(month)}-${pad2(i + 1)}`);
};

// ---- Reusable presentational pieces ------------------------------------------
// Bordered stat tile (mockup `.stat`): big number + status dot in the corner.
const StatTile = ({ value, label, dot }) => (
  <Box sx={{ border: '1px solid #eef0f4', borderRadius: '14px', p: { xs: 1.25, sm: 1.75 }, bgcolor: '#fafbfc' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography sx={{ fontFamily: NUM_FONT, fontWeight: 600, fontSize: { xs: '1.5rem', md: '1.75rem' }, lineHeight: 1, color: '#111827' }}>
        {value}
      </Typography>
      {dot && <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: dot, flexShrink: 0 }} />}
    </Box>
    <Typography sx={{ color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', mt: 1 }}>{label}</Typography>
  </Box>
);

// Bordered leave-balance tile (mockup `.bal .b`): big number + dotted label + sub.
const BalanceTile = ({ value, label, of, dot }) => (
  <Box sx={{ border: '1px solid #eef0f4', borderRadius: '14px', p: { xs: 1.5, sm: 1.85 }, bgcolor: '#fafbfc' }}>
    <Typography sx={{ fontFamily: NUM_FONT, fontWeight: 600, fontSize: '1.85rem', lineHeight: 1, color: '#111827' }}>{value}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, mt: 1 }}>
      {dot && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dot, flexShrink: 0 }} />}
      <Typography sx={{ color: '#6b7280', fontWeight: 600, fontSize: '0.78rem' }}>{label}</Typography>
    </Box>
    {of && <Typography sx={{ color: '#9ca3af', fontWeight: 500, fontSize: '0.7rem', mt: 0.3 }}>{of}</Typography>}
  </Box>
);

const SectionHeader = ({ title, icon, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2, flexWrap: 'wrap' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {icon}
      {/* Header typography unified with the Market Holidays widget (variant h6 / weight 600). */}
      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>{title}</Typography>
    </Box>
    {action}
  </Box>
);

const AttendanceDonut = ({ pct = 0, size = 130 }) => {
  const value = Math.max(0, Math.min(100, Math.round(pct)));
  return (
    <Box sx={{ width: size, height: size, borderRadius: '50%', background: `conic-gradient(#16a34a ${value * 3.6}deg, #e8edf3 0deg)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Box sx={{ width: size - 26, height: size - 26, borderRadius: '50%', bgcolor: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontFamily: NUM_FONT, fontWeight: 600, fontSize: '1.45rem', color: '#16a34a', lineHeight: 1 }}>{value}%</Typography>
        <Typography variant="caption" sx={{ color: '#6b7280' }}>Attendance</Typography>
      </Box>
    </Box>
  );
};

// FORTHEYE-style day pill: white tile with day number + status dot below.
const DayPill = ({ item }) => {
  const { iso, day, cell } = item;
  const [yy, mm, dd] = iso.split('-').map(Number);
  const weekday = WEEKDAYS[new Date(yy, mm - 1, dd).getDay()];
  const isSunday = new Date(yy, mm - 1, dd).getDay() === 0;
  const isFuture = iso > todayISO();
  const status = cell?.status || (isSunday ? 'HOLIDAY' : null);
  let dot = status ? (DOT_COLOR[status] || '#cbd5e1') : null;
  if (!dot && !isFuture) dot = '#dde1e7';
  const isToday = iso === todayISO();
  const meta = STATUS_META[status];
  return (
    <Tooltip arrow title={meta ? `${meta.label}${cell?.in_time ? ` · in ${cell.in_time}` : ''}` : ''}>
      <Box sx={{
        width: '100%', height: { xs: 46, sm: 54 }, borderRadius: '11px',
        bgcolor: isToday ? '#eef4ff' : (isSunday ? '#f5f6f8' : '#fff'),
        border: isToday ? `1.5px solid ${ACCENT}` : (isSunday ? '1px solid transparent' : '1px solid #eef0f4'),
        boxShadow: isToday || isSunday ? 'none' : '0 1px 2px rgba(17,24,39,0.05)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.3,
        opacity: isFuture ? 0.5 : 1,
      }}>
        <Typography sx={{ fontWeight: 700, fontSize: { xs: 8, sm: 9.5 }, color: '#9ca3af', lineHeight: 1, textTransform: 'uppercase', letterSpacing: 0.3 }}>{weekday}</Typography>
        <Typography sx={{ fontFamily: NUM_FONT, fontWeight: 600, fontSize: { xs: 13, sm: 15 }, color: '#374151', lineHeight: 1 }}>{day}</Typography>
        <Box sx={{ width: 5.5, height: 5.5, borderRadius: '50%', bgcolor: dot || 'transparent' }} />
      </Box>
    </Tooltip>
  );
};

const PerfTile = ({ emoji, message, value, unit, label, msgColor }) => (
  <Box sx={{ p: 1.75, borderRadius: '16px', border: '1px solid #eef0f4', bgcolor: '#fcfcfd', height: '100%' }}>
    <Typography variant="caption" sx={{ color: msgColor || '#6b7280', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, lineHeight: 1.3 }}>
      <span>{emoji}</span><span>{message}</span>
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4, mt: 1 }}>
      <Typography sx={{ fontFamily: NUM_FONT, fontWeight: 600, fontSize: '1.6rem', color: '#111827', lineHeight: 1 }}>{value}</Typography>
      {unit && <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#6b7280' }}>{unit}</Typography>}
    </Box>
    <Typography variant="caption" sx={{ color: '#6b7280' }}>{label}</Typography>
  </Box>
);

const Emphome = () => {
  const navigate = useNavigate();
  const { authData } = useContext(AuthContext);
  const { setLeaveDetail, leave } = useContext(leaveContext);
  const employeeId = authData?.employee_no;

  const today = new Date();
  const [periodMode, setPeriodMode] = useState('cycle');
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const currentCycleParam = useMemo(() => getCurrentCycle().cycle, []);
  const [activeCycle, setActiveCycle] = useState(currentCycleParam); // navigable cycle for the calendar

  // ---- Data ----
  const { data: employeeData, isLoading: empLoading } = useGetSingleEmployeeDetails(employeeId);
  const { data: leaveList } = useSingleEmpLeaveDetails(employeeId);
  // Always-current cycle: drives the profile check-in + the Performance card (stays put).
  const { data: currentCycle } = useEmployeeCycleAttendance(employeeId, { cycle: currentCycleParam });
  // Navigable cycle: drives the Attendance Summary counters + calendar (prev ↔ current).
  const { data: activeCycleData } = useEmployeeCycleAttendance(periodMode === 'cycle' ? employeeId : null, { cycle: activeCycle });
  const monthQuery = useEmployeeAttendanceHistory(periodMode === 'month' ? employeeId : null, { month, year });

  const attendance = periodMode === 'cycle' ? activeCycleData : monthQuery.data;
  // True when the navigable cycle is the live (current) one — disables the › arrow.
  const atCurrentCycle = (() => {
    const a = getCycleByParam(activeCycle);
    const c = getCycleByParam(currentCycleParam);
    return a.startYear === c.startYear && a.startMonth === c.startMonth;
  })();

  const emp = employeeData?.employee || employeeData;

  useEffect(() => {
    if (!emp) return;
    const balance = emp.employment_status === 'Confirmed'
      ? ((Number(emp.sick_leaves) || 0) + (Number(emp.casual_leaves) || 0))
      : (Number(emp.total_leaves) || 0);
    setLeaveDetail(balance);
  }, [emp, setLeaveDetail]);

  const totals = attendance?.totals || {};
  // HO-file only: non-HO employees get 0 from the backend, and the tile shows ₹0.
  const foodAllowance = totals.food_allowance_amount || 0;

  // One continuous run of day pills (26 → 25 for the cycle), no month split.
  const calendarDays = useMemo(() => {
    const days = attendance?.days;
    const dates = periodMode === 'month'
      ? monthDates(year, month)
      : (attendance?.dates || []);
    return dates.map((iso) => ({ iso, day: Number(iso.slice(8, 10)), cell: days?.[iso] || null }));
  }, [periodMode, attendance, month, year]);

  // Split the run of days into exactly 3 left-to-right rows.
  const calendarRows = useMemo(() => {
    const per = Math.ceil(calendarDays.length / 3) || 1;
    return [
      calendarDays.slice(0, per),
      calendarDays.slice(per, per * 2),
      calendarDays.slice(per * 2),
    ].filter((r) => r.length);
  }, [calendarDays]);

  const periodLabel = periodMode === 'cycle'
    ? (activeCycleData?.label || getCycleByParam(activeCycle).label)
    : `${MONTH_NAMES[month - 1]} ${year}`;

  // Work-hour insights from the current cycle (drives the Performance card).
  const { weeks, avgMinutes, lastWeekMinutes } = useMemo(() => {
    const days = currentCycle?.days || {};
    const dates = currentCycle?.dates || [];
    let total = 0; let worked = 0;
    const chunks = [];
    for (let i = 0; i < dates.length; i += 7) chunks.push(dates.slice(i, i + 7));
    const wk = chunks.map((grp, idx) => ({
      label: `Week ${idx + 1}`,
      minutes: grp.reduce((s, iso) => s + durationToMinutes(days[iso]?.work_duration), 0),
    }));
    Object.values(days).forEach((c) => {
      const mins = durationToMinutes(c?.work_duration);
      if (mins > 0) { total += mins; worked += 1; }
    });
    return {
      weeks: wk,
      avgMinutes: worked ? total / worked : 0,
      lastWeekMinutes: wk.length ? wk[wk.length - 1].minutes : 0,
    };
  }, [currentCycle]);

  const maxWeekMin = Math.max(1, ...weeks.map((w) => w.minutes));

  // Feedback message keyed to the average working hours per day.
  const avgHrs = avgMinutes / 60;
  const avgFeedback = avgMinutes === 0
    ? { emoji: '🕒', message: 'No hours logged yet', color: '#6b7280' }
    : avgHrs >= 8.5
      ? { emoji: '🌟', message: 'Excellent work hours!', color: '#16a34a' }
      : avgHrs >= 8
        ? { emoji: '⭐', message: 'You are doing Great!', color: '#2563eb' }
        : avgHrs >= 7
          ? { emoji: '👍', message: 'Good, keep it steady', color: '#0d9488' }
          : avgHrs >= 6
            ? { emoji: '⏳', message: 'A little low — log more time', color: '#f59e0b' }
            : { emoji: '🔴', message: 'Below average, please log more', color: '#ef4444' };

  const tenure = useMemo(() => {
    const doj = parseFlexibleDate(emp?.date_of_joining || emp?.doj);
    if (!doj) return null;
    const totalDays = Math.floor((new Date() - doj) / 86400000);
    if (totalDays < 0) return { years: 0, months: 0 };
    // Round by elapsed days (avg month ≈ 30.44) so "almost N months" reads as N.
    const totalMonths = Math.max(0, Math.round(totalDays / 30.44));
    return { years: Math.floor(totalMonths / 12), months: totalMonths % 12 };
  }, [emp]);

  // TEMP DEMO: the TEST001 account has no real leave data (and its leave endpoint
  // uses an <int> route), so seed two sample requests purely to preview the UI.
  // Remove this fallback once a real test employee with leaves exists.
  const DEMO_LEAVES = [
    { id: 'demo-approved', leave_type: 'Casual Leave', start_date: '2026-06-18', end_date: '2026-06-19', status: 'ACCEPTED' },
    { id: 'demo-pending', leave_type: 'Sick Leave', start_date: '2026-06-22', end_date: '2026-06-22', status: 'PENDING' },
  ];
  const realLeaves = Array.isArray(leaveList) ? leaveList.slice(0, 3) : [];
  const recentLeaves = realLeaves.length
    ? realLeaves
    : (emp?.employee_no === 'TEST001' ? DEMO_LEAVES : []);
  const isConfirmed = emp?.employment_status === 'Confirmed' || emp?.employment_status === 'Active';

  const shiftPeriod = (delta) => {
    if (periodMode === 'cycle') {
      const b = getCycleByParam(activeCycle);
      const idx = (b.startMonth - 1) + delta;
      const nm = ((idx % 12) + 12) % 12 + 1;
      const ny = b.startYear + Math.floor(idx / 12);
      setActiveCycle(getCycleByStart(nm, ny).cycle);
    } else if (periodMode === 'month') {
      const idx = (month - 1) + delta;
      setMonth(((idx % 12) + 12) % 12 + 1);
      setYear((y) => y + Math.floor(idx / 12));
    }
  };

  if (empLoading && !emp) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <LinearProgress />
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {[...Array(5)].map((_, i) => (
            <Grid item xs={12} lg={i === 0 ? 12 : i % 2 ? 7 : 5} key={i}>
              <Skeleton variant="rounded" height={i === 0 ? 96 : 300} sx={{ borderRadius: '22px' }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minWidth: 0, pb: { xs: 4, md: 2 } }}>
      {/* ---- Row 1: Attendance Summary + Leave ---- */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
        {/* Attendance Summary */}
        <Grid item xs={12} md={6} lg={5}>
          <Paper elevation={0} sx={CARD_SX}>
            {/* Counters (mockup `.stats` tiles) */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: { xs: 1, sm: 1.5 }, mb: 2 }}>
              <StatTile value={totals.present || 0} label="Total Present" dot={DOT_COLOR.PRESENT} />
              <StatTile value={totals.leave || 0} label="Total Leaves" dot={DOT_COLOR.LEAVE} />
              <StatTile value={totals.half_day || 0} label="Half Days" dot={DOT_COLOR.HALF_DAY} />
              <StatTile value={totals.holiday || 0} label="Weekends" dot={DOT_COLOR.HOLIDAY} />
            </Box>

            {/* Food allowance banner (mockup `.allowance`) — ₹0 for non-HO staff */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1.5, background: 'linear-gradient(100deg,#FFF7E6,#FFFBF0)', border: '1px solid #F6E4B8', borderRadius: '14px', px: { xs: 1.5, sm: 2 }, py: 1.5, mb: 2.5 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: '11px', bgcolor: '#f59e0b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <MonetizationOnIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: NUM_FONT, fontWeight: 600, color: '#111827', fontSize: '1.15rem', lineHeight: 1.1 }}>₹{foodAllowance}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#92700E', fontWeight: 600 }}>Food Allowance</Typography>
              </Box>
              <Box sx={{ ml: 'auto', fontSize: '0.7rem', fontWeight: 600, color: '#92700E', bgcolor: '#FBEBC4', px: 1.25, py: 0.5, borderRadius: '999px', whiteSpace: 'nowrap' }}>
                {periodMode === 'cycle' ? 'This cycle' : 'This month'}
              </Box>
            </Box>

            <SectionHeader
              title="Attendance Summary"
              icon={<EventAvailableIcon sx={{ color: ACCENT }} />}
              action={(
                <ToggleButtonGroup
                  size="small" exclusive value={periodMode} onChange={(e, v) => v && setPeriodMode(v)}
                  sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, fontSize: 12, py: 0.4, px: 1.4, border: '1px solid #e5e7eb', color: '#6b7280' }, '& .Mui-selected': { bgcolor: '#111827 !important', color: '#fff !important' } }}
                >
                  <ToggleButton value="cycle">Cycle</ToggleButton>
                  <ToggleButton value="month">Monthly</ToggleButton>
                </ToggleButtonGroup>
              )}
            />

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <IconButton size="small" onClick={() => shiftPeriod(-1)} sx={{ border: '1px solid #e5e7eb' }}><ChevronLeftIcon fontSize="small" /></IconButton>
              <Typography sx={{ fontWeight: 800, color: '#374151' }}>{periodLabel}</Typography>
              <IconButton
                size="small"
                disabled={periodMode === 'cycle' && atCurrentCycle}
                onClick={() => shiftPeriod(1)}
                sx={{ border: '1px solid #e5e7eb' }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
              {periodMode === 'cycle' && !atCurrentCycle && (
                <Button size="small" onClick={() => setActiveCycle(currentCycleParam)} sx={{ textTransform: 'none', fontWeight: 700, color: ACCENT, py: 0, minWidth: 0 }}>
                  Current
                </Button>
              )}
            </Stack>

            {calendarDays.length ? (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(7, minmax(0, 1fr))', sm: `repeat(${calendarRows[0]?.length || 1}, minmax(0, 1fr))` }, gap: { xs: 0.6, sm: 0.9 } }}>
                  {calendarDays.map((item) => <DayPill key={item.iso} item={item} />)}
                </Box>

                {/* Legend */}
                <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap sx={{ mt: 2.5 }}>
                  {[
                    { label: 'Present', color: DOT_COLOR.PRESENT },
                    { label: 'Leave / Absent', color: DOT_COLOR.LEAVE },
                    { label: 'Half Day', color: DOT_COLOR.HALF_DAY },
                    { label: 'Weekend / Holiday', color: DOT_COLOR.HOLIDAY },
                  ].map((l) => (
                    <Stack key={l.label} direction="row" spacing={0.75} alignItems="center">
                      <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: l.color }} />
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>{l.label}</Typography>
                    </Stack>
                  ))}
                </Stack>

                {/* Cycle attendance progress */}
                <Box sx={{ mt: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                    <Typography sx={{ fontWeight: 700, color: '#374151', fontSize: '0.85rem' }}>Attendance this cycle</Typography>
                    <Typography sx={{ fontFamily: NUM_FONT, fontWeight: 600, color: '#16a34a' }}>{Math.round(attendance?.attendance_pct ?? 0)}%</Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, Math.round(attendance?.attendance_pct ?? 0))}
                    sx={{ height: 8, borderRadius: 999, bgcolor: '#eef2f7', '& .MuiLinearProgress-bar': { borderRadius: 999, backgroundColor: '#16a34a' } }}
                  />
                </Box>
              </>
            ) : (
              <Box sx={{ py: 5, textAlign: 'center', color: '#9ca3af' }}>
                <Typography variant="body2">No attendance recorded for this period yet.</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Leave */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper elevation={0} sx={CARD_SX}>
            {/* Leave balances (mockup `.bal` tiles) */}
            <SectionHeader title="Leave Balance" icon={<EventAvailableIcon sx={{ color: ACCENT }} />} />
            <Box sx={{ display: 'grid', gridTemplateColumns: isConfirmed ? '1fr 1fr' : '1fr', gap: 1.5, mb: 2 }}>
              {isConfirmed ? (
                <>
                  <BalanceTile value={Number(emp?.sick_leaves || 0).toFixed(1)} label="Sick Leaves" of="Paid sick leave" dot="#16a34a" />
                  <BalanceTile value={Number(emp?.casual_leaves || 0).toFixed(1)} label="Casual Leaves" of="Paid casual leave" dot="#eab308" />
                </>
              ) : (
                <BalanceTile value={Number(leave ?? emp?.total_leaves ?? 0).toFixed(1)} label="Available Leaves" of="Probation balance" dot="#16a34a" />
              )}
            </Box>

            {/* Apply CTA (mockup `.apply`) */}
            <Button
              fullWidth
              startIcon={<AddIcon />}
              onClick={() => navigate('/leaves')}
              sx={{ bgcolor: ACCENT, color: '#fff', borderRadius: '12px', py: 1.25, fontWeight: 600, textTransform: 'none', mb: 2.5, boxShadow: '0 6px 16px rgba(47,107,255,0.25)', '&:hover': { bgcolor: '#2559e6', boxShadow: '0 8px 20px rgba(47,107,255,0.32)' } }}
            >
              Apply for Leave
            </Button>

            {/* Recent requests header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.75 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>Recent Requests</Typography>
              <Chip
                icon={<CalendarMonthIcon sx={{ fontSize: 16, color: '#6b7280 !important' }} />}
                label={`${MONTH_NAMES[today.getMonth()]} ${today.getFullYear()}`}
                sx={{ fontWeight: 700, fontSize: '0.74rem', bgcolor: '#f3f4f6', color: '#374151', borderRadius: '10px' }}
              />
            </Box>

            <Stack spacing={1.5}>
              {recentLeaves.length ? recentLeaves.map((req) => {
                const days = Math.max(1, Math.ceil((new Date(req.end_date) - new Date(req.start_date)) / 86400000) + 1);
                const s = parseFlexibleDate(req.start_date);
                const e = parseFlexibleDate(req.end_date);
                const shortMo = (d) => MONTH_NAMES[d.getMonth()].slice(0, 3);
                const rangeTag = s && e
                  ? (s.getTime() === e.getTime() ? `${s.getDate()} ${shortMo(s)}` : `${s.getDate()} – ${e.getDate()} ${shortMo(e)}`)
                  : '';
                const isPending = req.status === 'PENDING';
                const isDeclined = ['DECLINED', 'REJECTED'].includes(req.status);
                const isCancelled = ['CANCELLED', 'WITHDRAWN'].includes(req.status);
                const st = isPending
                  ? { label: 'Pending', fg: '#b45309', bg: '#fffbeb' }
                  : isDeclined
                    ? { label: 'Declined', fg: '#ef4444', bg: '#fef2f2' }
                    : isCancelled
                      ? { label: req.status === 'WITHDRAWN' ? 'Withdrawn' : 'Cancelled', fg: '#6b7280', bg: '#f1f3f6' }
                      : { label: 'Approved', fg: '#16a34a', bg: '#ecfdf5' };
                const sub = req.reason || (s ? `${MONTH_NAMES[s.getMonth()]} ${s.getFullYear()}` : '');
                return (
                  <Box
                    key={req.id}
                    onClick={() => navigate('/leaves')}
                    sx={{
                      display: 'flex', alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: { xs: 1, sm: 1.75 }, p: { xs: 1.25, sm: 1.5 },
                      borderRadius: '14px', border: '1px solid #eef0f4', bgcolor: '#fff', cursor: 'pointer',
                      transition: 'border-color .15s, box-shadow .15s',
                      '&:hover': { borderColor: '#E3E6EC', boxShadow: '0 1px 3px rgba(17,24,39,0.06)' },
                    }}
                  >
                    {/* Day-count square */}
                    <Box sx={{ width: 46, height: 46, borderRadius: '12px', bgcolor: '#f1f3f6', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ fontFamily: NUM_FONT, fontWeight: 600, fontSize: '1.15rem', lineHeight: 1, color: '#111827' }}>{days}</Typography>
                      <Typography sx={{ fontSize: '0.52rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: '#9ca3af', fontWeight: 700 }}>{days === 1 ? 'day' : 'days'}</Typography>
                    </Box>
                    {/* Info */}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#111827' }} noWrap>{req.leave_type || 'Leave'}</Typography>
                        {rangeTag && <Box sx={{ fontSize: '0.62rem', fontWeight: 600, px: 0.85, py: 0.15, borderRadius: '999px', bgcolor: '#f1f3f6', color: '#586173' }}>{rangeTag}</Box>}
                      </Box>
                      {sub && <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 500, mt: 0.3 }} noWrap>{sub}</Typography>}
                    </Box>
                    {/* Status pill */}
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.55, px: 1.1, py: 0.5, borderRadius: '999px', bgcolor: st.bg, flexShrink: 0, ml: { xs: 'auto', sm: 0 } }}>
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: st.fg }} />
                      <Typography sx={{ fontWeight: 600, fontSize: '0.7rem', color: st.fg }}>{st.label}</Typography>
                    </Box>
                  </Box>
                );
              }) : (
                <Box sx={{ py: 4, textAlign: 'center', color: '#9ca3af' }}>
                  <Typography variant="body2">No leave requests yet.</Typography>
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Market Holidays */}
        <Grid item xs={12} md={12} lg={3}>
          <MarketHolidaysWidget title="Market Holidays" showTabs={true} radius="22px" />
        </Grid>
      </Grid>

      {/* ---- Row 2: Celebrations + Performance ---- */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
        {/* Birthday & Anniversary */}
        <Grid item xs={12} lg={5}>
          <CelebrationsWidget />
        </Grid>

        {/* Performance */}
        <Grid item xs={12} lg={7}>
          <Paper elevation={0} sx={CARD_SX}>
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
              <Grid item xs={12} sm={4}>
                <PerfTile
                  emoji={lastWeekMinutes / 60 >= 40 ? '🔥' : '🔴'}
                  message={lastWeekMinutes / 60 >= 40 ? 'Great hours logged!' : 'Below average time, please log m...'}
                  msgColor={lastWeekMinutes / 60 >= 40 ? '#16a34a' : '#ef4444'}
                  value={formatHours(lastWeekMinutes)} unit="hr" label="Total Hours this week"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <PerfTile emoji={avgFeedback.emoji} message={avgFeedback.message} msgColor={avgFeedback.color} value={formatHours(avgMinutes)} unit="hr" label="Average Working Hours" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <PerfTile
                  emoji="🎉" message="Congrats on your work anniversary!" msgColor="#a855f7"
                  value={tenure ? (tenure.years > 0 ? tenure.years : tenure.months) : '—'}
                  unit={tenure ? (tenure.years > 0 ? (tenure.years === 1 ? 'yr' : 'yrs') : 'mo') : ''}
                  label="Total Years Served"
                />
              </Grid>
            </Grid>

            <SectionHeader title="Performance" icon={<InsightsIcon sx={{ color: ACCENT }} />} />

            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={7}>
                {weeks.length ? (
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, height: 170 }}>
                    {weeks.map((w, i) => {
                      const isCurrent = i === weeks.length - 1;
                      return (
                        <Box key={w.label} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: isCurrent ? ACCENT : '#6b7280' }}>{formatHours(w.minutes)}h</Typography>
                          <Box sx={{ width: '100%', display: 'flex', alignItems: 'flex-end', height: 120, bgcolor: '#f6f8fb', borderRadius: '12px', p: 0.5 }}>
                            <Box sx={{ width: '100%', height: `${Math.max(6, (w.minutes / maxWeekMin) * 100)}%`, borderRadius: '10px', background: isCurrent ? 'linear-gradient(180deg,#5b8def,#2f6bff)' : 'linear-gradient(180deg,#c7d7f5,#9bb8ef)' }} />
                          </Box>
                          <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 600 }}>{w.label}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Box sx={{ py: 4, textAlign: 'center', color: '#9ca3af' }}>
                    <Typography variant="body2">No work hours recorded this cycle yet.</Typography>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={5} sx={{ display: 'flex', justifyContent: 'center' }}>
                <AttendanceDonut pct={currentCycle?.attendance_pct ?? 0} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Emphome;
